import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoneyValue } from './MoneyValue';
import { QtyStepper } from './QtyStepper';
import { cn } from '@/lib/utils';
import type { Bundle } from '@/types/coupon';
import type { OrderBundleItem } from '@/types/sale';

interface BundleBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundle: Bundle | null;
  onConfirm: (item: OrderBundleItem) => void;
}

/** FIXED bundles add directly; FLEXIBLE bundles enforce min/max item picks. */
export function BundleBuilder({ open, onOpenChange, bundle, onConfirm }: BundleBuilderProps) {
  const [picks, setPicks] = useState<Record<string, number>>({});
  const isFlexible = bundle?.bundleType === 'FLEXIBLE';

  useEffect(() => {
    if (open) setPicks({});
  }, [open]);

  const totalPicked = useMemo(
    () => Object.values(picks).reduce((s, n) => s + n, 0),
    [picks],
  );

  const min = bundle?.minItems ?? 0;
  const max = bundle?.maxItems ?? 99;
  const canConfirm = !isFlexible || (totalPicked >= min && totalPicked <= max);

  const confirm = () => {
    if (!bundle) return;
    const selectedItems = isFlexible
      ? Object.entries(picks)
          .filter(([, q]) => q > 0)
          .map(([productId, quantity]) => ({ productId, quantity }))
      : undefined;
    onConfirm({ bundleId: bundle.id, quantity: 1, addOnItems: [], selectedItems });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {bundle?.name}
            <Badge variant={isFlexible ? 'gold' : 'secondary'}>{bundle?.bundleType}</Badge>
          </DialogTitle>
          <DialogDescription>
            {isFlexible
              ? `Pilih ${min}–${max} item (${totalPicked} terpilih).`
              : 'Paket tetap — item sudah ditentukan.'}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[50vh] space-y-2.5 overflow-y-auto pos-scroll">
          {(bundle?.items ?? []).map((it) => (
            <div
              key={it.productId}
              className="flex items-center justify-between gap-3 rounded-lg border-2 border-border bg-card p-3"
            >
              <div className="min-w-0">
                <p className="truncate font-semibold text-ink-900">{it.name}</p>
                <MoneyValue value={it.price} className="text-sm" />
              </div>
              {isFlexible ? (
                <QtyStepper
                  value={picks[it.productId] ?? 0}
                  max={max}
                  onChange={(v) => setPicks((prev) => ({ ...prev, [it.productId]: v }))}
                />
              ) : (
                <Badge variant="outline" className="tnum">
                  × {it.quantity ?? 1}
                </Badge>
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className={cn('text-sm font-semibold', canConfirm ? 'text-ink-600' : 'text-warning')}>
            Harga paket: <MoneyValue value={bundle?.price} emphasis className="text-base" />
          </span>
          <Button onClick={confirm} disabled={!canConfirm}>
            Tambah paket
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
